// frontend/src/pages/SupportTicketPage.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Pagination
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  PriorityHigh as PriorityIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: '1px solid #e8e8e8',
}));

const StatusChip = styled(Chip)(({ status }) => {
  const colors = {
    'Open': { bg: '#ffebee', color: '#c62828' },
    'In Progress': { bg: '#fff3e0', color: '#ef6c00' },
    'Resolved': { bg: '#e8f5e9', color: '#2e7d32' },
    'Closed': { bg: '#e0e0e0', color: '#616161' }
  };
  return {
    backgroundColor: colors[status]?.bg || '#f5f5f5',
    color: colors[status]?.color || '#333',
    fontWeight: 500,
    fontSize: '12px'
  };
});

const PriorityChip = styled(Chip)(({ priority }) => {
  const colors = {
    'Low': { bg: '#e8f5e9', color: '#2e7d32' },
    'Medium': { bg: '#fff3e0', color: '#ef6c00' },
    'High': { bg: '#ffebee', color: '#c62828' },
    'Urgent': { bg: '#fce4ec', color: '#d81b60' }
  };
  return {
    backgroundColor: colors[priority]?.bg || '#f5f5f5',
    color: colors[priority]?.color || '#333',
    fontWeight: 500,
    fontSize: '12px'
  };
});

function SupportTicketPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({ status: 'all', category: 'all', priority: 'all', search: '' });
  const [pagination, setPagination] = useState({ page: 1, rowsPerPage: 10 });
  
  // Form state
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'Other',
    priority: 'Medium'
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch tickets on load and filter change
  useEffect(() => {
    fetchTickets();
  }, [filters]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        ...filters,
        page: pagination.page,
        limit: pagination.rowsPerPage
      });
      
      const response = await axios.get(`${API_URL}/api/support/tickets/my?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      setTickets(response.data.data || []);
    } catch (error) {
      showNotification('Failed to fetch tickets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.description) {
      showNotification('Please fill all required fields', 'error');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/support/tickets`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.success) {
        showNotification('Ticket created successfully!', 'success');
        setFormData({ subject: '', description: '', category: 'Other', priority: 'Medium' });
        fetchTickets();
        setActiveTab(1); // Switch to My Tickets tab
      }
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to create ticket', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/support/tickets/${ticketId}/status`, 
        { status: 'Closed' ,
           headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      showNotification('Ticket closed successfully', 'success');
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setDetailDialogOpen(false);
      }
    } catch (error) {
      showNotification('Failed to close ticket', 'error');
    }
  };

  const handleCancelTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to cancel this ticket? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      showNotification('Ticket cancelled successfully', 'success');
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setDetailDialogOpen(false);
      }
    } catch (error) {
      showNotification('Failed to cancel ticket', 'error');
    }
  };

  const viewTicketDetails = async (ticket) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/support/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSelectedTicket(response.data.data);
      setDetailDialogOpen(true);
    } catch (error) {
      showNotification('Failed to load ticket details', 'error');
    }
  };

  const showNotification = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const categories = ['Profile', 'IT', 'Payroll', 'HR', 'Attendance', 'Leave', 'Other'];
  const priorities = ['Low', 'Medium', 'High', 'Urgent'];

  return (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Typography variant="h4" fontWeight="600" sx={{ mb: 3, color: '#1a1a1a' }}>
        Support Center
      </Typography>
      
      <Paper sx={{ borderRadius: '16px', overflow: 'hidden' }}>
               <Tabs
          value={activeTab}
          onChange={(e, newVal) => setActiveTab(newVal)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}
        >
          <Tab icon={<AddIcon />} label="Raise New Ticket" />
          <Tab icon={<ViewIcon />} label="My Tickets" />
        </Tabs>

        
        {/* Raise New Ticket Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <form onSubmit={handleSubmitTicket}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief summary of your issue"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
  <FormControl fullWidth>
    <InputLabel id="category-label">Category</InputLabel>
    <Select
      labelId="category-label"
      label="Category"
      value={formData.category}
      onChange={(e) =>
        setFormData({ ...formData, category: e.target.value })
      }
    >
      {categories.map((cat) => (
        <MenuItem key={cat} value={cat}>
          {cat}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Grid>

<Grid item xs={12} sm={6}>
  <FormControl fullWidth>
    <InputLabel id="priority-label">Priority</InputLabel>
    <Select
      labelId="priority-label"
      label="Priority"
      value={formData.priority}
      onChange={(e) =>
        setFormData({ ...formData, priority: e.target.value })
      }
    >
      {priorities.map((pri) => (
        <MenuItem key={pri} value={pri}>
          <PriorityChip priority={pri} label={pri} size="small" />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    required
                    multiline
                    rows={6}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please provide detailed information about your issue..."
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => setFormData({ subject: '', description: '', category: 'Other', priority: 'Medium' })}
                    >
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                      sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                    >
                      {submitting ? <CircularProgress size={24} /> : 'Submit Ticket'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        )}
        
        {/* My Tickets Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            {/* Filters */}
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
              <TextField
                size="small"
                placeholder="Search by ticket # or subject..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                sx={{ width: 250 }}
              />
           <FormControl size="small" sx={{ minWidth: 120 }}>
  <InputLabel id="status-label">Status</InputLabel>
  <Select
    labelId="status-label"
    label="Status"
    value={filters.status}
    onChange={(e) =>
      setFilters({ ...filters, status: e.target.value })
    }
  >
    <MenuItem value="all">All</MenuItem>
    <MenuItem value="Open">Open</MenuItem>
    <MenuItem value="In Progress">In Progress</MenuItem>
    <MenuItem value="Resolved">Resolved</MenuItem>
    <MenuItem value="Closed">Closed</MenuItem>
  </Select>
</FormControl>

<FormControl size="small" sx={{ minWidth: 120 }}>
  <InputLabel id="filter-category-label">Category</InputLabel>
  <Select
    labelId="filter-category-label"
    label="Category"
    value={filters.category}
    onChange={(e) =>
      setFilters({ ...filters, category: e.target.value })
    }
  >
    <MenuItem value="all">All</MenuItem>
    {categories.map((cat) => (
      <MenuItem key={cat} value={cat}>
        {cat}
      </MenuItem>
    ))}
  </Select>
</FormControl>
              
              <Button onClick={fetchTickets} startIcon={<RefreshIcon />} variant="outlined" size="small">
                Refresh
              </Button>
            </Box>
            
            {/* Tickets Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead sx={{ bgcolor: '#fafafa' }}>
                  <TableRow>
                    <TableCell>Ticket #</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : tickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">No tickets found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tickets.map((ticket) => (
                      <TableRow key={ticket.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            {ticket.ticket_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            {ticket.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={ticket.category} />
                        </TableCell>
                        <TableCell>
                          <PriorityChip priority={ticket.priority} label={ticket.priority} size="small" />
                        </TableCell>
                        <TableCell>
                          <StatusChip status={ticket.status} label={ticket.status} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(ticket.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => viewTicketDetails(ticket)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {ticket.status === 'Open' && (
                            <Tooltip title="Cancel Ticket">
                              <IconButton size="small" color="error" onClick={() => handleCancelTicket(ticket.id)}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {ticket.status === 'Resolved' && (
                            <Tooltip title="Close Ticket">
                              <IconButton size="small" color="success" onClick={() => handleCloseTicket(ticket.id)}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
      
      {/* Ticket Details Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{selectedTicket.ticket_number}</Typography>
                <StatusChip status={selectedTicket.status} label={selectedTicket.status} />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Subject</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedTicket.subject}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Category</Typography>
                  <Chip label={selectedTicket.category} size="small" />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Priority</Typography>
                  <PriorityChip priority={selectedTicket.priority} label={selectedTicket.priority} />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedTicket.description}
                    </Typography>
                  </Paper>
                </Grid>
                
                {selectedTicket.resolution && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Resolution</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                      <Typography variant="body2">{selectedTicket.resolution}</Typography>
                    </Paper>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Divider />
                  <Box display="flex" gap={2} mt={2}>
                    <Typography variant="caption" color="textSecondary">
                      Created: {new Date(selectedTicket.createdAt).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Last Updated: {new Date(selectedTicket.updatedAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {selectedTicket.status === 'Open' && (
                <Button color="error" onClick={() => handleCancelTicket(selectedTicket.id)}>
                  Cancel Ticket
                </Button>
              )}
              {selectedTicket.status === 'Resolved' && (
                <Button color="success" onClick={() => handleCloseTicket(selectedTicket.id)}>
                  Close Ticket
                </Button>
              )}
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default SupportTicketPage;